import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Inject,
  OnDestroy,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  MsalService,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import { filter, takeUntil } from 'rxjs/operators';
import { InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { Subject, Subscription } from 'rxjs';

import { DialogSettingsComponent } from './dialog-settings/dialog-settings.component';
import { DialogNewAccidentComponent } from '@app/shared/components/dialog-new-accident/dialog-new-accident.component';
import { DialogNewInjuryComponent } from '@app/shared/components/dialog-new-injury/dialog-new-injury.component';
import { NotificationModalComponent } from './notification-modal/notification-modal.component';
import { NotificationService } from '@app/services/notification.service';
import { Observable } from 'rxjs';

import { ProfileService } from '../../services/profile.service';
import { AuthService } from '@app/services/auth.service';
import { PermissionService } from '@app/services/permission.service';
import { getBackgroundClass, getInitials } from '@app/shared/helpers/functions.helper';
import { DialogProfileComponent } from './dialog-profile/dialog-profile.component';
import { OrganizationService } from '@app/services/organization.service';
import { AccidentDialogStateService } from '@app/shared/services/accident-dialog-state.service';
import { InjuryDialogStateService } from '@app/shared/services/injury-dialog-state.service';

@Component({
  selector: 'app-sub-header',
  templateUrl: './sub-header.component.html',
  styleUrls: ['./sub-header.component.scss'],
  standalone: false,
})
export class SubHeaderComponent implements OnInit, OnDestroy {
  @ViewChild('notificationBtn', { read: ElementRef }) notificationBtn!: ElementRef;
  @ViewChild('mobileNotificationBtn', { read: ElementRef }) mobileNotificationBtn!: ElementRef;
  @Input() isExpanded!: boolean;
  @Input() type?: string = '';
  @Output() menuItemClicked: EventEmitter<boolean> = new EventEmitter();
  circleColor = '';
  photoUrl = null;
  name = '';
  userData!: any;

  isDropdownMenuOpened = false;
  dialogRef: MatDialogRef<DialogProfileComponent> | null = null;
  notificationDialogRef: MatDialogRef<NotificationModalComponent> | null = null;

  loginDisplay = false;
  isAccidentDialogMinimized = false;
  isInjuryDialogMinimized = false;

  organizations: any[] = [];
  selectedOrganization: any = null;
  unreadCount$: Observable<number>;

  private readonly _destroying$ = new Subject<void>();
  private subscriptions: Subscription = new Subscription();

  private readonly colors = [
    '#EB7181', // red
    '#468547', // green
    '#FFD558', // yellow
    '#3670B2', // blue
  ];

  getBackgroundClass = getBackgroundClass;
  getInitials = getInitials;

  constructor(
    private profileService: ProfileService,
    private dialog: MatDialog,
    private msalService: MsalService,
    private broadcastService: MsalBroadcastService,
    private authService: AuthService,
    private permissionService: PermissionService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private organizationService: OrganizationService,
    private accidentDialogStateService: AccidentDialogStateService,
    private injuryDialogStateService: InjuryDialogStateService,
    private notificationService: NotificationService
  ) {
    this.unreadCount$ = this.notificationService.getUnreadCount();
    this.broadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
      });
  }

  get isMobile(): boolean {
    return this.type === 'mobile';
  }

  async ngOnInit(): Promise<void> {
    this.authService
      .ensureMsalInitialized()
      .then(() => {
        this.getB2cObjectId();
        this.getUserInfoFromLocalStorage();
        this.profileService.getProfile().subscribe((data) => {
          this.setLoginDisplay();
        });
      })
      .catch((error: any) => {
        console.error('Error initializing MSAL:', error);
      });

    this.subscriptions.add(
      this.accidentDialogStateService.isDialogMinimized$.subscribe((isMinimized) => {
        this.isAccidentDialogMinimized = isMinimized;
      })
    );

    this.subscriptions.add(
      this.injuryDialogStateService.isDialogMinimized$.subscribe((isMinimized) => {
        this.isInjuryDialogMinimized = isMinimized;
      })
    );

    await this.loadSelectedOrganization();
    this.getUserOrganizations();

    // Reflect external organization changes (e.g., from Analytics Events URL proceed action)
    this.subscriptions.add(
      this.organizationService.organization$.subscribe((organization) => {
        if (organization) {
          // Prefer using the instance from the dropdown options to satisfy mat-select identity check
          const match = this.organizations.find(
            (org) => org.organizationId === organization.organizationId
          );
          this.selectedOrganization = match || organization;
          this.saveSelectedOrganization();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();

    this.subscriptions.unsubscribe();
  }

  async loadSelectedOrganization(): Promise<void> {
    let retries = 20;

    while (retries > 0) {
      const storedOrganization = localStorage.getItem('selectedOrganization');

      if (storedOrganization) {
        this.selectedOrganization = JSON.parse(storedOrganization);
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));
      retries--;
    }
  }

  getUserOrganizations(): void {
    const storedData = localStorage.getItem('userData');
    if (storedData) {
      const parsedData = JSON.parse(storedData);

      // Filter organizations to include only those with a permissions array
      this.organizations = (
        parsedData?.permissions?.organizationPermissions || []
      ).filter(
        (org: any) => Array.isArray(org.permissions) && org.permissions.length > 0
      );

      // Sort organizations alphabetically by name
      this.organizations.sort((a: any, b: any) =>
        a.organizationName.localeCompare(b.organizationName)
      );

      // If there is only one organization, select it by default
      if (this.organizations.length === 1) {
        this.selectedOrganization = this.organizations[0];
        this.saveSelectedOrganization();
      } else if (this.selectedOrganization) {
        // Ensure that the selected organization is an object from the list
        const matchingOrganization = this.organizations.find(
          (org) => org.organizationId === this.selectedOrganization.organizationId
        );
        if (matchingOrganization) {
          this.selectedOrganization = matchingOrganization;
        }
      } else {
        // If there is no selected organization, select the first one
        this.selectedOrganization = this.organizations[0];
        this.saveSelectedOrganization();
      }
    }
  }

  onOrganizationChange(newOrganization: any): void {
    this.selectedOrganization = newOrganization.value;
    this.saveSelectedOrganization();
    this.organizationService.changeOrganization(this.selectedOrganization);
  }

  saveSelectedOrganization(): void {
    localStorage.setItem(
      'selectedOrganization',
      JSON.stringify(this.selectedOrganization)
    );
    // this.updateVisibleTabs();
  }

  getUserInfoFromLocalStorage(): void {
    setTimeout(() => {
      const storedData = localStorage.getItem('userData');
      if (storedData) {
        this.userData = JSON.parse(storedData);
        this.photoUrl = this.userData.userInfo.previewUrl;
        this.name = `${this.userData.userInfo.firstName} ${this.userData.userInfo.lastName}`;
      }
    }, 1000);
  }

  onMenuItemClick(item: string = ''): void {
    this.isDropdownMenuOpened = false;
    switch (item) {
      case 'settings':
        this.dialog.open(DialogSettingsComponent, {
          panelClass: 'single',
          maxWidth: '450px',
          width: '100%',
          autoFocus: false,
        });
        break;
      default:
        this.menuItemClicked.next(true);
        break;
    }
  }

  openAccountProfileModal(): void {
    this.isDropdownMenuOpened = false;
    this.dialogRef = this.dialog.open(DialogProfileComponent, {
      panelClass: 'single',
      maxWidth: '660px',
      width: '100%',
      autoFocus: false,
    });
    this.dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.getUserInfoFromLocalStorage();
      }
    });
  }

  onNewAccidentClick(): void {
    if (!this.isAccidentDialogMinimized) {
      this.dialog.open(DialogNewAccidentComponent, {
        panelClass: 'single',
        maxWidth: '800px',
        width: '100%',
        autoFocus: false,
      });
    }
  }

  onNewInjuryClick(): void {
    if (!this.isInjuryDialogMinimized) {
      this.dialog.open(DialogNewInjuryComponent, {
        panelClass: 'single',
        maxWidth: '800px',
        width: '100%',
        autoFocus: false,
      });
    }
  }

  onProfileMenuToggle(): void {
    this.isDropdownMenuOpened = !this.isDropdownMenuOpened;
  }

  login(): void {
    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.msalService.loginRedirect();
    }
  }

  logout(): void {
    this.authService.logout();
  }

  setLoginDisplay(): void {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
  }

  getB2cObjectId(): void {
    this.authService.getB2cObjectId().subscribe({
      next: (objectId) => {
        if (objectId) {
          this.loadUserPermissionsAndProfile(objectId);
          // this.fetchUserImage(objectId);
        }
      },
      error: (error) => console.log(error),
    });
  }

  loadUserPermissionsAndProfile(objectId: string): void {
    this.permissionService.loadUserData(objectId).subscribe({
      next: (userData) => {
        this.name = `${userData.userInfo.firstName} ${userData.userInfo.lastName}`;
        console.log('User data loaded', userData);
      },
      error: (error) => console.log('Error loading user data:', error),
    });
  }

  canCreateAccidents(): boolean {
    return this.permissionService.hasPermission('WRITE', 'Accidents');
  }

  canCreateInjury(): boolean {
    return this.permissionService.hasPermission('WRITE', 'Injuries');
  }

  openNotifications(): void {
    if (this.notificationDialogRef) {
      this.notificationDialogRef.close();
      this.notificationDialogRef = null;
      return;
    }

    const buttonRef = this.isMobile ? this.mobileNotificationBtn : this.notificationBtn;
    if (!buttonRef) return;
    
    const buttonElement = buttonRef.nativeElement as HTMLElement;
    const rect = buttonElement.getBoundingClientRect();
    
    const config = this.isMobile ? {
      panelClass: 'notification-dialog',
      width: '100vw',
      maxWidth: '100vw',
      autoFocus: false,
      hasBackdrop: true
    } : {
      panelClass: 'notification-dialog',
      width: '456px',
      maxWidth: '456px',
      position: { 
        right: `${window.innerWidth - rect.right}px`, 
        top: `${rect.bottom + 8}px` 
      },
      autoFocus: false,
      hasBackdrop: false
    };
    
    this.notificationDialogRef = this.dialog.open(NotificationModalComponent, config);
    this.notificationDialogRef.afterClosed().subscribe(() => {
      this.notificationDialogRef = null;
    });
  }
}
